import capitalize from 'lodash/capitalize';
import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { formatDate } from '@hotelplan/libs.utils';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { IFdrFlightSectionProps } from './fdr-flight-section.types';

const FdrAirlineShortLogo = styled.img(
  sx2CssThemeFn({
    mr: 2,
    width: '75px',
    flexShrink: 0,
    objectFit: `contain`,
  })
);

const FdrDepartureAirportWrapper = styled.div(
  ({ theme: { space, colors, FONT_SIZE } }) =>
    sx2CssThemeFn({
      display: 'flex',
      flexDirection: 'column',
      marginBottom: space[2],

      '.departure-airport': {
        color: colors.darkGray,
        ...FONT_SIZE.MEDIUM,
        marginBottom: space[2],
      },
    })
);

const FdrDepartureDate = styled.span(({ theme: { FONT_SIZE } }) =>
  sx2CssThemeFn({
    ...FONT_SIZE.MEDIUM,
    textAlign: 'right',
  })
);

const FdrTimeInfo = styled.div(({ theme: { space, FONT_SIZE } }) =>
  sx2CssThemeFn({
    display: 'flex',
    ...FONT_SIZE.MEDIUM,
    alignItems: 'center',
    flexWrap: 'wrap',

    '.time-delimiter': {
      marginLeft: space[1],
      marginRight: space[1],
    },

    '.stops-count': {
      marginLeft: space[1],
    },
  })
);

const FdrFlightSectionShort: React.FC<IFdrFlightSectionProps> = props => {
  const {
    airlineLogo,
    departureTime,
    arrivalAirport,
    departureAirport,
    departureAirportPlaceholder,
    stopLocations,
    arrivalTime,
    departureDate,
    isReturn,
    displayAirportName = true,
    displayArrivalAirport = false,
    className,
  } = props;

  const [t, i18n] = useTranslation('orl');

  const stopsCount = stopLocations.length;
  const { mobile } = useDeviceType();

  return (
    <div className={className}>
      <FdrDepartureAirportWrapper>
        <p className="departure-airport">
          {displayArrivalAirport ? (
            <>{`${departureAirport} - ${arrivalAirport}`}</>
          ) : (
            <>
              {`${
                displayAirportName
                  ? `${departureAirportPlaceholder} (${departureAirport})`
                  : departureAirport
              }`}
            </>
          )}
        </p>
        <FdrAirlineShortLogo
          src={airlineLogo}
          className="airport-logo"
          alt="airline logo"
        />
      </FdrDepartureAirportWrapper>
      <FdrDepartureDate
        data-id={`flight-departure-date${isReturn ? '-out' : '-in'}`}
        className="departure-date"
      >
        {capitalize(
          formatDate(
            departureDate,
            mobile ? 'dd.MM.yyyy' : 'EE dd.MM.yyyy',
            i18n.language
          )
        )}
      </FdrDepartureDate>
      <FdrTimeInfo className="time-info">
        <span data-id={`flight-departure-time${isReturn ? '-out' : '-in'}`}>
          {departureTime}
        </span>
        <div className="time-delimiter">–</div>
        <span data-id={`flight-arrival-time${isReturn ? '-out' : '-in'}`}>
          {arrivalTime}
        </span>
        ,
        <span className="stops-count">
          {stopsCount === 0
            ? t('flight.direct')
            : `${t('stops.count', {
                count: stopsCount,
              })} ${t('flight.stopsIn')} ${stopLocations.join('/')}`}
        </span>
      </FdrTimeInfo>
    </div>
  );
};

export default FdrFlightSectionShort;
